import { addDays, addMinutes, startOfDay } from 'date-fns';
import { SAMPLE_DEMO_CLIENTS } from './fixtures/sampleClients';
import { DEMO_APPOINTMENT_SOURCE } from './constants';
import { getDefaultAppointmentStatus } from '../utils/appointmentWorkflow';
import { getStatusColor } from '../utils/appointmentUtils';
import {
  getCompanyDayHoursForDate,
  isRangeWithinCompanyHours,
} from '../shared/booking/company-hours/companyHoursBounds';
import {
  getEffectiveScheduleForDate,
  slotFitsEmployeeSchedule,
} from '../shared/booking/employee-schedule/workingSchedule';

const DEMO_DAY_COUNT = 7;
const CURSOR_STEP_MIN = 15;
const CALENDAR_SLOT_MIN = 30;

/** Status variety for screenshot demos */
const SLOT_META_PATTERN = [
  { status: 'confirmed', awaitingClientConfirmation: true },
  { status: 'confirmed', awaitingClientConfirmation: true },
  { status: 'confirmed' },
  { status: 'confirmed', awaitingClientConfirmation: true },
  { status: 'confirmed' },
  { status: 'completed' },
];

const pickRotating = (items, index) => items[index % items.length];

const DEFAULT_SEED_DAY_HOURS = {
  isOpen: true,
  open: '9:00 AM',
  close: '5:00 PM',
};

function resolveEffectiveSeedHours(companyHours) {
  if (companyHours && Object.keys(companyHours).length > 0) {
    return companyHours;
  }

  return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    .reduce((hours, dayKey) => {
      hours[dayKey] = { ...DEFAULT_SEED_DAY_HOURS };
      return hours;
    }, {});
}

function intervalsOverlap(startA, endA, startB, endB) {
  return startA < endB && endA > startB;
}

function getServiceTotalDuration(service) {
  return (parseInt(service.duration, 10) || 60) + (parseInt(service.bufferTime, 10) || 0);
}

function getShortestServiceDuration(services) {
  return Math.min(...services.map(getServiceTotalDuration));
}

function pickClientForSlot(staffIndex, dayOffset, slotIndex) {
  const clientIndex =
    staffIndex * 997 +
    dayOffset * 43 +
    slotIndex * 13 +
    (staffIndex + dayOffset) * 7;
  return pickRotating(SAMPLE_DEMO_CLIENTS, clientIndex);
}

function pickServiceForRemainingTime(services, remainingMinutes, slotIndex) {
  if (remainingMinutes <= 0 || !services.length) return null;

  const startOffset = slotIndex % services.length;
  let bestFit = null;

  for (let i = 0; i < services.length; i += 1) {
    const service = services[(startOffset + i) % services.length];
    const totalDuration = getServiceTotalDuration(service);
    if (totalDuration <= remainingMinutes) {
      if (!bestFit || totalDuration > getServiceTotalDuration(bestFit)) {
        bestFit = service;
      }
    }
  }

  if (bestFit) return bestFit;

  const sortedByDuration = [...services].sort(
    (a, b) => getServiceTotalDuration(a) - getServiceTotalDuration(b)
  );

  return sortedByDuration.find(
    (service) => getServiceTotalDuration(service) <= remainingMinutes
  ) || null;
}

function slotIsValid({
  dateTime,
  endTime,
  bounds,
  employee,
  effectiveCompanyHours,
  booked,
}) {
  if (!isRangeWithinCompanyHours(dateTime, endTime, bounds)) {
    return false;
  }

  if (!slotFitsEmployeeSchedule({
    workingSchedule: employee.workingSchedule,
    date: dateTime,
    slotStart: dateTime,
    slotEnd: endTime,
    companyHours: effectiveCompanyHours,
  })) {
    return false;
  }

  if (booked.some((b) => intervalsOverlap(dateTime, endTime, b.start, b.end))) {
    return false;
  }

  return true;
}

function buildAppointmentPayload({
  companyId,
  demoBatchId,
  timezone,
  defaultStatus,
  index,
  client,
  service,
  employee,
  dateTime,
  endTime,
  duration,
  slotMeta,
}) {
  const status = slotMeta.status || defaultStatus;
  const awaitingClientConfirmation = slotMeta.awaitingClientConfirmation === true
    && status !== 'completed'
    && status !== 'cancelled';
  const serviceDuration = parseInt(service.duration, 10) || 60;
  const serviceBufferTime = parseInt(service.bufferTime, 10) || 0;
  const totalDuration = serviceDuration + serviceBufferTime;
  const servicePrice = parseFloat(service.price) || 0;
  const employeeName = employee.displayName || employee.name || 'Staff';

  return {
    companyId,
    demoBatchId,
    source: DEMO_APPOINTMENT_SOURCE,
    bookingType: 'manual',
    publicShare: false,
    reminderSent: false,
    reminderSMSSent: false,
    clientId: `demo-client-${index + 1}`,
    clientName: client.name,
    clientEmail: client.email,
    clientPhone: client.phone,
    dateTime,
    endTime,
    duration,
    timezone,
    service: {
      id: service.id,
      name: service.name,
      duration: serviceDuration,
      bufferTime: serviceBufferTime,
      totalDuration,
      price: servicePrice,
      description: service.description || '',
    },
    serviceId: service.id,
    serviceName: service.name,
    serviceDuration,
    serviceBufferTime,
    serviceTotalDuration: totalDuration,
    servicePrice,
    serviceDescription: service.description || '',
    selectedAddons: [],
    selectedPackage: null,
    employee: {
      id: employee.id,
      name: employeeName,
      title: employee.isOwner ? 'Owner' : (employee.title || 'Staff Member'),
      isOwner: Boolean(employee.isOwner),
    },
    employeeId: employee.id,
    employeeName,
    employeeTitle: employee.isOwner ? 'Owner' : (employee.title || 'Staff Member'),
    employeeIsOwner: Boolean(employee.isOwner),
    staffId: employee.id,
    status,
    color: getStatusColor(status),
    notes: 'Demo appointment for screenshots — safe to remove.',
    totalPrice: servicePrice,
    depositAmount: null,
    depositPaid: false,
    balanceDue: servicePrice,
    paymentStatus: status === 'completed' ? 'paid' : 'unpaid',
    clientConfirmed: awaitingClientConfirmation ? false : true,
  };
}

function packAppointmentsForInterval({
  interval,
  bounds,
  employee,
  effectiveCompanyHours,
  services,
  staffIndex,
  dayOffset,
  booked,
  payloads,
  payloadContext,
  globalSlotIndexRef,
}) {
  let cursor = new Date(interval.start);
  const intervalEnd = interval.end.getTime();
  const minServiceDuration = getShortestServiceDuration(services);
  let idleSteps = 0;
  const maxIdleSteps = Math.ceil(
    (intervalEnd - cursor.getTime()) / (CURSOR_STEP_MIN * 60 * 1000)
  ) + 1;

  while (cursor.getTime() < intervalEnd) {
    const remainingMinutes = (intervalEnd - cursor.getTime()) / (60 * 1000);

    if (remainingMinutes < minServiceDuration) {
      break;
    }

    const service = pickServiceForRemainingTime(
      services,
      remainingMinutes,
      globalSlotIndexRef.value
    );

    if (!service) {
      const nextCursor = addMinutes(cursor, CURSOR_STEP_MIN);
      if (nextCursor.getTime() >= intervalEnd) break;
      cursor = nextCursor;
      idleSteps += 1;
      if (idleSteps > maxIdleSteps) break;
      continue;
    }

    const totalDuration = getServiceTotalDuration(service);
    const dateTime = new Date(cursor);
    const endTime = addMinutes(dateTime, totalDuration);

    if (
      slotIsValid({
        dateTime,
        endTime,
        bounds,
        employee,
        effectiveCompanyHours,
        booked,
      })
    ) {
      const index = globalSlotIndexRef.value;
      const slotMeta = pickRotating(SLOT_META_PATTERN, index);
      const client = pickClientForSlot(staffIndex, dayOffset, index);

      payloads.push(buildAppointmentPayload({
        ...payloadContext,
        index,
        client,
        service,
        employee,
        dateTime,
        endTime,
        duration: totalDuration,
        slotMeta,
      }));

      booked.push({ start: dateTime, end: endTime });
      cursor = endTime;
      globalSlotIndexRef.value += 1;
      idleSteps = 0;
      continue;
    }

    const nextCursor = addMinutes(cursor, CALENDAR_SLOT_MIN);
    if (nextCursor.getTime() >= intervalEnd) break;
    cursor = nextCursor;
    idleSteps += 1;
    if (idleSteps > maxIdleSteps) break;
  }
}

export function buildDemoAppointmentPayloads({
  companyId,
  anchorDate,
  staff,
  services,
  demoBatchId,
  companyHours = {},
}) {
  const dayStart = startOfDay(anchorDate instanceof Date ? anchorDate : new Date(anchorDate));
  const defaultStatus = getDefaultAppointmentStatus();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const effectiveCompanyHours = resolveEffectiveSeedHours(companyHours);
  const globalSlotIndexRef = { value: 0 };

  const payloadContext = {
    companyId,
    demoBatchId,
    timezone,
    defaultStatus,
  };

  return staff.flatMap((employee, staffIndex) => {
    const payloads = [];

    for (let dayOffset = 0; dayOffset < DEMO_DAY_COUNT; dayOffset += 1) {
      const slotDay = addDays(dayStart, dayOffset);
      const bounds = getCompanyDayHoursForDate(slotDay, effectiveCompanyHours);
      if (!bounds?.openTime || !bounds?.closeTime) {
        continue;
      }

      const schedule = getEffectiveScheduleForDate({
        workingSchedule: employee.workingSchedule,
        date: slotDay,
        companyHours: effectiveCompanyHours,
      });

      if (!schedule.isWorking || !schedule.intervals.length) {
        continue;
      }

      const booked = [];

      schedule.intervals.forEach((interval) => {
        packAppointmentsForInterval({
          interval,
          bounds,
          employee,
          effectiveCompanyHours,
          services,
          staffIndex,
          dayOffset,
          booked,
          payloads,
          payloadContext,
          globalSlotIndexRef,
        });
      });
    }

    return payloads;
  });
}
